import {
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BulkCompileApiService,
  BulkCompileResult,
  BulkCompileRun,
  isBulkCompileRunTerminal,
} from '@sportello-balneari/api-client';
import { EMPTY, Subscription, catchError, interval, switchMap } from 'rxjs';
import { extractApiMessage } from '../../api/api-error-message';
import { CompilingApiService } from '../../api/compiling-api.service';
import { EVALUATION_CAPABILITIES } from '../evaluation-capabilities';
import { CompilingFormService } from './compiling-form.service';
import { AnswerPermissionsService } from './state/answer-permissions.service';
import { AutoCompileStateService } from './state/auto-compile-state.service';
import { TenderContextService } from './state/tender-context.service';
import {
  applyCompiledAnswer,
  collectCompilableQuestions,
  describeApplyFailure,
  hasProposal,
  previewCompiledAnswer,
  type CompilableQuestion,
} from './apply-compiled-answer';

/**
 * `empty-only` maps to `overwriteExisting: false` — the backend compiles only
 * the questions still blank. `overwrite` recompiles everything.
 */
export type BulkCompileMode = 'empty-only' | 'overwrite';

/** How often a run in flight is asked how it is getting on. */
export const BULK_COMPILE_POLL_INTERVAL_MS = 3000;

export interface BulkCompileSuggestion extends CompilableQuestion {
  /** What the applicant will see in the field once applied. */
  preview: string | null;
  justification: string | null;
  payload: BulkCompileResult['compiled'][number]['answer'];
}

export interface BulkCompileProblem extends CompilableQuestion {
  message: string;
}

/**
 * A question the batch answered but proposed nothing for: a closed question
 * with no supporting evidence, an open one with an empty draft. The
 * justification is the model's reason, and is what the applicant needs to
 * read. Not a problem — retrying will not help, the documents simply do not
 * cover it.
 */
export interface BulkCompileNoProposal extends CompilableQuestion {
  justification: string | null;
}

export interface BulkCompileOutcome {
  /** Have the `compiled` suggestions been written into the form already? */
  applied: boolean;
  compiled: BulkCompileSuggestion[];
  /** Answered by the batch with nothing to apply; the form is left alone. */
  noProposal: BulkCompileNoProposal[];
  /**
   * Expected when the mode was `empty-only`: not an error, just a number the UI
   * mentions in passing.
   */
  alreadyAnsweredCount: number;
  /**
   * Widget types the backend cannot compile — these need filling in by hand.
   * Empty today (MultiRadioButton is compiled group by group, the
   * MultiplierRadioButton as one pick plus a quantity), kept for the next
   * widget type the agent cannot serve.
   */
  unsupported: CompilableQuestion[];
  /**
   * Questions the batch failed on, plus suggestions the form refused (an option
   * that no longer exists). One list because they have one remedy: retry the
   * question with its own button. A question answered with "nothing" is not
   * here but in `noProposal` — retrying it would find the same nothing.
   */
  problems: BulkCompileProblem[];
}

/**
 * Drives "compila tutte le risposte" for one tender answer.
 *
 * The batch itself runs server-side, as a background job, and this only watches
 * it: starting a run returns straight away, leaving the page no longer cancels
 * anything, and closing the tab costs nothing. What comes back is still a set of
 * *proposals* — nothing is written to the form until the applicant says so in the
 * review dialog.
 *
 * It reports no progress of its own and offers no cancel: both belong to the
 * global run tracker, which follows the applicant off this page, and drawing them
 * here as well put one run on screen twice.
 *
 * **Nothing is applied automatically**, not even in `empty-only` mode where the
 * synchronous version used to. A run takes minutes and the applicant may have
 * typed into those very questions while it went; writing over that silently, on
 * their return, is not a trade worth making for one saved click.
 *
 * Component-scoped like the rest of `provideCompiling()`: the run survives this
 * service, but the form it proposes answers for does not.
 */
@Injectable()
export class BulkCompileService {
  private readonly api = inject(CompilingApiService);
  private readonly runs = inject(BulkCompileApiService);
  private readonly forms = inject(CompilingFormService);
  private readonly tenderContext = inject(TenderContextService);
  private readonly autoCompileState = inject(AutoCompileStateService);
  private readonly capabilities = inject(EVALUATION_CAPABILITIES);
  private readonly permissions = inject(AnswerPermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  /** The run being watched, or null when there is none. */
  private readonly currentRun = signal<BulkCompileRun | null>(null);

  readonly outcome = signal<BulkCompileOutcome | null>(null);
  readonly error = signal<string | null>(null);

  /**
   * Bumped whenever something asks for the review to be shown again.
   *
   * The dialog lives in `BulkCompileStatusComponent` and the button that reopens
   * it in `BulkCompileButtonComponent`; a counter on the service they share is
   * what lets one ask the other without either knowing it exists. A counter and
   * not a boolean, so asking twice in a row still reopens a dialog the applicant
   * closed in between.
   */
  readonly reviewRequests = signal(0);

  /** How many proposals are waiting to be applied. 0 when there is no review. */
  readonly pendingReviewCount = computed(() => {
    const outcome = this.outcome();
    return outcome && !outcome.applied ? outcome.compiled.length : 0;
  });

  /**
   * A *bulk* run is in flight. Distinct from `AutoCompileStateService.busy`,
   * which any compile sets: this one drives the minutes-long progress panel,
   * which a single-question compile should not.
   */
  readonly running = computed(() => {
    if (this.starting()) return true;

    const run = this.currentRun();
    return !!run && !isBulkCompileRunTerminal(run.status);
  });

  /**
   * The start request is in flight but the run does not exist yet.
   *
   * Folded into `running` so the trigger disables on the first click. Without it there is a window of
   * one round trip in which a second click posts a second run, which the server refuses — correctly,
   * but with an error the applicant did nothing to deserve.
   */
  private readonly starting = signal(false);

  private readonly questions = computed(() =>
    collectCompilableQuestions(this.forms.moduleStructure()),
  );

  private readonly questionsById = computed(
    () => new Map(this.questions().map((q) => [q.widgetId, q])),
  );

  /**
   * Whether this form has automatic compilation at all — the same condition the
   * per-question button uses, plus an endpoint that exists for this answer, a
   * rendering allowed to spend a full run on it (the demo is not) and a user
   * allowed to write the technical offer it fills in.
   */
  readonly available = computed(
    () =>
      this.capabilities.canBulkCompile &&
      this.permissions.canEditTechnicalOffer() &&
      this.api.supportsBulkCompile &&
      this.questions().length > 0,
  );

  readonly questionCount = computed(() => this.questions().length);

  private subscription: Subscription | null = null;
  private lastMode: BulkCompileMode = 'empty-only';

  constructor() {
    this.destroyRef.onDestroy(() => this.stopWatching());

    // The answer arrives after the service does, so this waits for it rather than
    // asking the host to remember to call `restore()`. Guarded on the id having
    // changed, not merely emitted: the context signal also fires on an ordinary
    // save, and re-fetching the run every time the applicant saves would reopen a
    // review they had just closed.
    let restoredFor: string | null = null;

    effect(() => {
      const id = this.tenderContext.tenderFormAnswer()?.id;
      if (!id || id === restoredFor) return;

      restoredFor = id;

      // `untracked` because `restore` reads the current run: tracking it would make this effect rerun
      // on every poll tick, for an early return each time.
      untracked(() => this.restore());
    });
  }

  /**
   * Picks up whatever the server already has for this answer.
   *
   * This is what makes a run survive the page: a batch that finished while the
   * applicant was elsewhere — or while the browser was closed — is found here,
   * with its suggestions, and goes straight to review.
   */
  restore(): void {
    const tenderFormAnswerId = this.tenderContext.tenderFormAnswer()?.id;
    if (!tenderFormAnswerId || this.currentRun()) return;

    this.runs
      .latest(tenderFormAnswerId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          if (run) this.adopt(run);
        },
        // Nothing to restore is indistinguishable from not being able to ask, and
        // neither is worth an error banner on a form the applicant just opened.
        error: () => undefined,
      });
  }

  clearError(): void {
    this.error.set(null);
  }

  /** Brings the review dialog back, after "Vai alla domanda" closed it. */
  requestReview(): void {
    this.reviewRequests.update((n) => n + 1);
  }

  /**
   * Discards the pending review. The run is acknowledged server-side, so it stops
   * following the applicant around in the global tracker. The proposals go; the
   * reasons the batch gave for proposing nothing stay with their questions.
   */
  dismissOutcome(): void {
    const outcome = this.outcome();
    if (outcome && !outcome.applied) this.recordNoProposals(outcome);

    this.outcome.set(null);
    this.acknowledge();
  }

  /** Re-runs the last attempt — the indexing-in-progress refusal is retryable. */
  retry(): void {
    this.currentRun.set(null);
    this.run(this.lastMode);
  }

  /** Queues a run. Returns at once: the batch itself happens server-side. */
  run(mode: BulkCompileMode): void {
    if (this.running()) return;

    const tenderFormAnswerId = this.tenderContext.tenderFormAnswer()?.id;
    if (!tenderFormAnswerId) {
      this.error.set('Impossibile compilare: contesto non disponibile.');
      return;
    }

    this.lastMode = mode;
    this.error.set(null);
    this.outcome.set(null);

    this.starting.set(true);

    this.runs
      .start(tenderFormAnswerId, {
        overwriteExisting: mode === 'overwrite',
        userInstructions: this.instructionsForCompilableQuestions(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (run) => {
          this.starting.set(false);
          this.adopt(run);
        },
        error: (err: unknown) => {
          this.starting.set(false);
          this.error.set(
            extractApiMessage(err) ??
              'Si è verificato un errore durante la compilazione automatica.',
          );
        },
      });
  }

  /**
   * Writes the pending suggestions into the form. Every control is marked dirty
   * — the batch saved nothing, so the applicant still has to save.
   */
  applySuggestions(): void {
    const outcome = this.outcome();
    if (!outcome || outcome.applied) return;

    const structure = this.forms.moduleStructure();
    const applied: BulkCompileSuggestion[] = [];
    const problems = [...outcome.problems];

    for (const suggestion of outcome.compiled) {
      const widget = structure?.[suggestion.widgetId];
      const result = applyCompiledAnswer(
        widget?._type === 'widget' ? widget : undefined,
        this.forms.form.controls[suggestion.widgetId],
        suggestion.payload,
      );

      if (result.status !== 'applied') {
        problems.push({ ...suggestion, message: describeApplyFailure(result) });
        continue;
      }

      applied.push(suggestion);
      this.autoCompileState.markAiCompiled(
        suggestion.widgetId,
        suggestion.justification,
      );
    }

    this.recordNoProposals(outcome);

    this.outcome.set({
      ...outcome,
      applied: true,
      compiled: applied,
      problems,
    });

    this.acknowledge();
  }

  /**
   * Leaves the model's reason next to each question it could not answer.
   *
   * Called whichever way the review ends, applied or dismissed: the review
   * decides about *proposals*, and these are not proposals — nothing is written
   * to the form, the applicant just gets to see why the field stayed as it was.
   * Recording them only on apply would lose them in the very case they matter
   * most, a run whose every result is "nothing found" and whose only button is
   * "Ok".
   */
  private recordNoProposals(outcome: BulkCompileOutcome): void {
    for (const entry of outcome.noProposal) {
      if (entry.justification?.trim()) {
        this.autoCompileState.markNoProposal(
          entry.widgetId,
          entry.justification,
        );
      }
    }
  }

  /**
   * Takes on a run and decides what to do about it: keep watching, or read its
   * result. Both the start call and every poll land here, so there is one place
   * that knows what each status means.
   */
  private adopt(run: BulkCompileRun): void {
    this.currentRun.set(run);

    if (!isBulkCompileRunTerminal(run.status)) {
      this.autoCompileState.busy.set(true);
      this.watch(run.runId);
      return;
    }

    this.stopWatching();
    this.autoCompileState.busy.set(false);

    if (run.status === 'Failed') {
      this.error.set(
        run.errorMessage ??
          'Si è verificato un errore durante la compilazione automatica.',
      );
      this.acknowledge();
      return;
    }

    // A cancelled run has nothing to review: the batch returns its answers in one
    // piece, so stopping it early leaves none.
    if (run.status === 'Cancelled') {
      this.acknowledge();
      return;
    }

    // `start` and `cancel` answer without the payload, so a completed run has to
    // be fetched once more to get at the suggestions.
    if (run.result) {
      this.outcome.set(this.toOutcome(run.result));
      return;
    }

    this.runs
      .get(run.runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (full) => {
          this.currentRun.set(full);
          if (full.result) this.outcome.set(this.toOutcome(full.result));
        },
        error: () =>
          this.error.set(
            'La compilazione è terminata ma i risultati non sono al momento disponibili. Riprova tra qualche istante.',
          ),
      });
  }

  /** Polls one run to completion. Idempotent: watching twice is still one poll. */
  private watch(runId: string): void {
    if (this.subscription) return;

    this.subscription = interval(BULK_COMPILE_POLL_INTERVAL_MS)
      .pipe(
        switchMap(() =>
          // A dropped poll is a dropped tick, nothing more — the next one asks
          // again. Letting it error would end the interval for good.
          this.runs.get(runId).pipe(catchError(() => EMPTY)),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((run) => this.adopt(run));
  }

  private stopWatching(): void {
    this.subscription?.unsubscribe();
    this.subscription = null;
  }

  /**
   * Tells the server the run has been dealt with, which is what removes it from
   * the global tracker. Fire-and-forget: a run that stays listed for a while
   * longer is a cosmetic problem, and there is nothing useful to say about it.
   */
  private acknowledge(): void {
    const runId = this.currentRun()?.runId;
    if (!runId) return;

    this.runs
      .acknowledge(runId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
  }

  /**
   * The instructions the applicant already typed per question (the ones saved
   * as `questionInstructions`), narrowed to widgets the batch will actually
   * look at.
   */
  private instructionsForCompilableQuestions(): { [widgetId: string]: string } {
    const compilable = this.questionsById();

    return Object.fromEntries(
      Object.entries(this.tenderContext.questionInstructions()).filter(
        ([widgetId, instructions]) =>
          compilable.has(widgetId) && !!instructions?.trim(),
      ),
    );
  }

  private toOutcome(response: BulkCompileResult): BulkCompileOutcome {
    const structure = this.forms.moduleStructure();

    const compiled: BulkCompileSuggestion[] = [];
    const noProposal: BulkCompileNoProposal[] = [];

    for (const entry of response.compiled ?? []) {
      const payload = entry.answer ?? {};

      // The backend lists every question it answered under `compiled`, including
      // the ones it answered with "nothing": a closed question the documents
      // give no evidence for comes back with a null `selectedValue` and a
      // justification saying so. Sorted on the payload rather than the widget
      // type, so an entry whose widget the structure no longer has still lands
      // in the right bucket. Applying one of these would only ever fail as
      // `empty`, and the review would count it among the proposals first.
      if (!hasProposal(payload)) {
        noProposal.push({
          ...this.describe(entry.widgetId),
          justification: payload.justification ?? null,
        });
        continue;
      }

      const widget = structure?.[entry.widgetId];

      compiled.push({
        ...this.describe(entry.widgetId),
        preview: previewCompiledAnswer(
          widget?._type === 'widget' ? widget : undefined,
          payload,
        ),
        justification: payload.justification ?? null,
        payload,
      });
    }

    const skipped = response.skipped ?? [];

    return {
      applied: false,
      compiled,
      noProposal,
      alreadyAnsweredCount: skipped.filter(
        (s) => s.reason === 'AlreadyAnswered',
      ).length,
      unsupported: skipped
        .filter((s) => s.reason !== 'AlreadyAnswered')
        .map((s) => this.describe(s.widgetId)),
      problems: (response.failed ?? []).map((f) => ({
        ...this.describe(f.widgetId),
        message: f.message,
      })),
    };
  }

  /**
   * The batch reports widget ids; the applicant needs the question text. A
   * widget the structure no longer knows (an older revision, say) still gets a
   * row rather than vanishing from the report — it just cannot be linked to.
   */
  private describe(widgetId: string): CompilableQuestion {
    return (
      this.questionsById().get(widgetId) ?? {
        widgetId,
        label: widgetId,
        sectionId: '',
        sectionTitle: '',
        subSectionId: '',
      }
    );
  }
}
